import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, Signal } from '@angular/core';
import { Permission } from '@core/permissions/models/permission.enum';
import { TranslatePipe } from '@ngx-translate/core';
import { Feature } from '@core/features/models/feature.enum';
import { NavigationFacade } from '@core/navigation/facades/navigation.facade';
import { TopNavigationPath } from '@core/navigation/models/top-navigation/top-navigation-path';
import { K365SetupGuidesAccessFacade } from '@layout/k365-setup-guides-access/facades/k365-setup-guides-access.facade';
import { AbsorbFacade } from '@layout/navigation/facades/absorb.facade';
import { PaymentManagementActiveFacade } from '@layout/payment-management-active/facades/payment-management-active.facade';
import { PartnerProgramActiveFacade } from '@layout/partner-program-active/facades/partner-program-active.facade';
import { PermissionsFacade } from '@core/permissions/facades/permissions.facade';
import {
  LeftNavigationExpandableItem,
  LeftNavigationExternalItem,
  LeftNavigationItem,
} from '@core/navigation/models/left-navigation/left-navigation-item.interface';
import { LeftNavigationIconClass } from '@core/navigation/models/left-navigation/left-navigation-icon-class.enum';
import { toSignal } from '@angular/core/rxjs-interop';
import { first, of } from 'rxjs';
import { SsoTargetPage } from '@design/open-sso-link/models/sso-target-page.enum';
import { SsoSalesforceDestination } from '@design/open-sso-link/models/sso-salesforce-destination.enum';
import { Country } from '@core/locations/models/country.enum';
import { LeftNavigationItemComponent } from '@layout/navigation/components/left-navigation-item/left-navigation-item.component';
import { LeftNavigationItemType } from '@core/navigation/models/left-navigation/left-navigation-item-type.enum';
import { LeftNavigationExpandableItemComponent } from '@layout/navigation/components/left-navigation-expandable-item/left-navigation-expandable-item.component';
import { LeftNavigation } from '@core/navigation/models/left-navigation/left-navigation.type';
import { MenuSettingsFacade } from '@layout/left-panel/facades/menu-settings.facade';
import { filterIsNotUndefined } from '@kaseya-utils';
import { environment } from '@env/environment';
import { KaseyaCommunityFacade } from '@core/sso/services/kaseya-community/kaseya-community.facade';
import { AuthorizationService } from '@core/authorization/services/authorization.service';
import { adminSettingsViewPermissions } from '@core/permissions/constants/permissions.const';

@Component({
  selector: 'itc-left-navigation',
  templateUrl: 'left-navigation.component.html',
  styleUrl: 'left-navigation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, LeftNavigationItemComponent, LeftNavigationExpandableItemComponent],
})
export class LeftNavigationComponent {
  private readonly authorizationService = inject(AuthorizationService);
  private readonly navigationFacade = inject(NavigationFacade);
  private readonly k365SetupGuidesAccessFacade = inject(K365SetupGuidesAccessFacade);
  private readonly absorbFacade = inject(AbsorbFacade);
  private readonly paymentManagementActiveFacade = inject(PaymentManagementActiveFacade);
  private readonly kaseyaCommunityFacade = inject(KaseyaCommunityFacade);
  private readonly partnerProgramActiveFacade = inject(PartnerProgramActiveFacade);
  private readonly permissionsFacade = inject(PermissionsFacade);
  private readonly menuSettingsFacade = inject(MenuSettingsFacade);
  private readonly hasOnboardingSubscriptionsView = this.permissionsFacade.hasPermission(Permission.OnboardingSubscriptionsView);
  private readonly hasAccessToPartnerProgram = this.permissionsFacade.hasPermission(Permission.PartnerProgram);
  private readonly isMigrated = this.authorizationService.isMigratedUser();

  readonly topNavigation = input<TopNavigationPath>();
  readonly isMobileView = input(false);
  readonly isMenuCollapsed = this.menuSettingsFacade.isMenuCollapsed;
  readonly activeTopNavigation = computed(() => this.topNavigation() ?? this.navigationFacade.activeTopNavigation());
  readonly navigationItemClick = output<void>();
  readonly Feature = Feature;
  readonly Permission = Permission;
  readonly LeftNavigationItemType = LeftNavigationItemType;
  readonly leftNavigation: LeftNavigation = {
    [TopNavigationPath.Home]: [
      {
        id: 'dashboard',
        type: LeftNavigationItemType.Link,
        iconClass: LeftNavigationIconClass.Dashboard,
      },
      {
        id: 'kaseya-365-setup-guide',
        type: LeftNavigationItemType.Link,
        iconClass: LeftNavigationIconClass.Kaseya365,
        permission: Permission.OnboardingSubscriptionsView,
        guard: this.isNotMigratedGuard(
          toSignal(this.k365SetupGuidesAccessFacade.setupGuidesAccess$.pipe(filterIsNotUndefined(), first()), { initialValue: false })
        ),
      },
      {
        id: 'executive-reports',
        type: LeftNavigationItemType.Link,
        iconClass: LeftNavigationIconClass.OrganizationDashboard,
        permission: Permission.ExecutiveReports,
      },
      {
        id: 'support',
        type: LeftNavigationItemType.Link,
        iconClass: LeftNavigationIconClass.Support,
        permission: Permission.Zendesk,
      },
      ...this.getSubscriptionAndBillingItems(),
      {
        id: 'learning',
        type: LeftNavigationItemType.Expandable,
        iconClass: LeftNavigationIconClass.Learning,
        children: [
          {
            id: 'professional-services',
            type: LeftNavigationItemType.Link,
            permission: Permission.ProfessionalServicesDashboard,
            guard: this.isNotMigratedGuard(),
          },
          {
            id: 'absorb',
            type: LeftNavigationItemType.ExternalLink,
            externalLink: this.absorbFacade.ssoLoginUri$,
            permission: Permission.Absorb,
          },
          {
            id: 'report-card',
            type: LeftNavigationItemType.Link,
            permission: [Permission.AbsorbLearningSummary, Permission.AbsorbUserCourses],
          },
        ],
      },
      {
        id: 'programs',
        type: LeftNavigationItemType.Expandable,
        iconClass: LeftNavigationIconClass.Programs,
        children: [
          {
            id: 'cyber-insurance',
            type: LeftNavigationItemType.ExternalLink,
            externalLink: of('https://kaseyacysurance.loroinsurance.com/products/370/details'),
            permission: Permission.CyberInsurance,
            accessibleCountries: [Country.US, Country.AU, Country.NZ],
          },
        ],
      },
      {
        id: 'partner-and-community',
        type: LeftNavigationItemType.Expandable,
        iconClass: LeftNavigationIconClass.PartnerAndCommunity,
        children: [
          {
            id: 'partner-program',
            type: LeftNavigationItemType.Link,
            permission: Permission.PartnerProgram,
            guard: toSignal(this.partnerProgramActiveFacade.isPartnerProgramActive$.pipe(filterIsNotUndefined()), { initialValue: false }),
          },
          {
            id: 'kaseya-community',
            type: LeftNavigationItemType.ExternalLink,
            externalLink: this.kaseyaCommunityFacade.loginUrl$,
            permission: Permission.Gainsight,
          },
          {
            id: 'kaseya-events',
            type: LeftNavigationItemType.ExternalLink,
            externalLink: of('https://events.kaseya.com/'),
          },
          {
            id: 'kaseya-swag',
            type: LeftNavigationItemType.ExternalLink,
            permission: Permission.KaseyaSwag,
            externalLink: of('https://merch.kaseya.com'),
          },
        ],
      },
    ],
    [TopNavigationPath.Automations]: [
      {
        id: 'automation-center',
        type: LeftNavigationItemType.Link,
        iconClass: LeftNavigationIconClass.AutomationCenter,
        permission: Permission.AutomationCenterView,
      },
      {
        id: 'cooper-insights',
        type: LeftNavigationItemType.Link,
        permission: Permission.InsightsView,
        iconClass: LeftNavigationIconClass.CooperInsights,
      },
      {
        id: 'cooper-bots',
        type: LeftNavigationItemType.Link,
        permission: Permission.CooperBotsView,
        iconClass: LeftNavigationIconClass.CooperBots,
      },
    ],
    [TopNavigationPath.Organizations]: [
      {
        id: 'executive-reports',
        type: LeftNavigationItemType.Link,
        iconClass: LeftNavigationIconClass.OrganizationDashboard,
        permission: Permission.ExecutiveReports,
      },
      {
        id: 'organization-mapping',
        type: LeftNavigationItemType.TaggedLink,
        permission: Permission.OrganizationMappingsView,
        iconClass: LeftNavigationIconClass.OrganizationMapping,
        feature: Feature.OrganizationMappings,
      },
    ],
    [TopNavigationPath.BillingAndSubscriptions]: [
      {
        id: 'invoices',
        type: LeftNavigationItemType.ExternalLink,
        iconClass: LeftNavigationIconClass.Invoices,
        permission: Permission.SalesforceECommerce,
        ssoSalesforceDestination: SsoSalesforceDestination.Invoices,
      },
      {
        id: 'subscriptions',
        type: LeftNavigationItemType.Link,
        iconClass: LeftNavigationIconClass.Subscriptions,
        permission: Permission.BillingAndSubscriptions,
      },
      {
        id: 'orders',
        type: LeftNavigationItemType.ExternalLink,
        iconClass: LeftNavigationIconClass.Orders,
        permission: Permission.SalesforceECommerce,
        ssoSalesforceDestination: SsoSalesforceDestination.Orders,
      },
      {
        id: 'payment-methods',
        type: LeftNavigationItemType.Link,
        iconClass: LeftNavigationIconClass.PaymentMethods,
        permission: Permission.PaymentManagementView,
      },
      {
        id: 'transactions',
        type: LeftNavigationItemType.ExternalLink,
        iconClass: LeftNavigationIconClass.Transactions,
        permission: Permission.SalesforceECommerce,
        ssoSalesforceDestination: SsoSalesforceDestination.Transactions,
      },
      {
        id: 'compare-invoices',
        type: LeftNavigationItemType.ExternalLink,
        iconClass: LeftNavigationIconClass.CompareInvoices,
        permission: Permission.InvoiceComparisonTool,
        externalLink: of(environment.invoiceComparisonToolUrl),
      },
      {
        id: 'request-tax-exemption',
        type: LeftNavigationItemType.ExternalLink,
        iconClass: LeftNavigationIconClass.RequestTaxExemption,
        permission: Permission.CertifyTaxModify,
        ssoCertifyTaxLink: true,
      },
    ],
    [TopNavigationPath.AdminSettings]: [
      {
        id: 'setup-summary',
        type: LeftNavigationItemType.Link,
        iconClass: LeftNavigationIconClass.SetupSummary,
        permission: adminSettingsViewPermissions.setupSummary,
      },
      {
        id: 'third-party-idp',
        type: LeftNavigationItemType.Link,
        iconClass: LeftNavigationIconClass.ThirdPartyIdp,
        permission: adminSettingsViewPermissions.thirdPartyIdp,
      },
      {
        id: 'security',
        type: LeftNavigationItemType.Link,
        iconClass: LeftNavigationIconClass.Security,
        permission: adminSettingsViewPermissions.security,
      },
      {
        id: 'module-management',
        type: LeftNavigationItemType.Link,
        iconClass: LeftNavigationIconClass.Settings,
        permission: adminSettingsViewPermissions.moduleManagement,
      },
      {
        id: 'access-groups',
        type: LeftNavigationItemType.Link,
        iconClass: LeftNavigationIconClass.AccessGroups,
        permission: adminSettingsViewPermissions.accessGroups,
      },
      {
        id: 'user-management',
        type: LeftNavigationItemType.Link,
        iconClass: LeftNavigationIconClass.User,
        permission: adminSettingsViewPermissions.userManagement,
      },
      {
        id: 'role-management',
        type: LeftNavigationItemType.TaggedLink,
        iconClass: LeftNavigationIconClass.RoleManagement,
        feature: Feature.CustomRoles,
        tagTextTranslationKey: 'navigation.tags.new',
        permission: adminSettingsViewPermissions.roleManagement,
      },
    ],
    [TopNavigationPath.UserSettings]: [
      {
        id: 'profile',
        type: LeftNavigationItemType.Link,
        iconClass: LeftNavigationIconClass.User,
        permission: Permission.UserProfileRead,
      },
      {
        id: 'module-management',
        type: LeftNavigationItemType.Link,
        permission: Permission.LauncherModulesView,
        iconClass: LeftNavigationIconClass.ModuleUrls,
      },
      {
        id: 'security',
        type: LeftNavigationItemType.Link,
        iconClass: LeftNavigationIconClass.Security,
        permission: Permission.PushNotifications,
      },
    ],
  };

  constructor() {
    this.loadPartnerProgramActiveWhenPermitted();
    this.navigationFacade.setLeftNavigation(this.leftNavigation);
    this.navigationFacade.updateTopNavigationItems();
    this.loadK365SetupGuidesAccessWhenPermitted();
  }

  asLeftNavigationExpandableItem(data: unknown): LeftNavigationExpandableItem {
    return data as LeftNavigationExpandableItem;
  }

  private loadPartnerProgramActiveWhenPermitted(): void {
    effect(() => {
      if (!this.isMigrated && this.hasAccessToPartnerProgram()) {
        this.partnerProgramActiveFacade.loadPartnerProgramActiveIfNotMigrated();
      } else {
        this.partnerProgramActiveFacade.setPartnerProgramInactive();
      }
    });
  }

  private loadK365SetupGuidesAccessWhenPermitted(): void {
    effect(() => {
      if (!this.hasOnboardingSubscriptionsView() || this.isMigrated) {
        this.k365SetupGuidesAccessFacade.setSetupGuidesAccessInactive();
        return;
      }

      this.k365SetupGuidesAccessFacade.loadSetupGuidesAccessIfNotLoaded();
    });
  }

  private isNotMigratedGuard(guard: Signal<boolean> = signal(true)): Signal<boolean> {
    return computed(() => !this.isMigrated && guard());
  }

  private getSubscriptionAndBillingItems(): (LeftNavigationItem | LeftNavigationExternalItem | LeftNavigationExpandableItem)[] {
    if (!this.isMigrated) {
      return [
        {
          id: 'subscriptions-and-billing',
          type: LeftNavigationItemType.Expandable,
          iconClass: LeftNavigationIconClass.SubscriptionsAndBilling,
          guard: this.isNotMigratedGuard(),
          children: [
            {
              id: 'billing-dashboard',
              type: LeftNavigationItemType.ExternalLink,
              permission: Permission.BillingAndSubscriptions,
              ssoDeepLink: '/',
              guard: this.isNotMigratedGuard(),
            },
            {
              id: 'view-and-pay-invoices',
              type: LeftNavigationItemType.ExternalLink,
              permission: Permission.BillingAndSubscriptions,
              ssoTargetPage: SsoTargetPage.Billing,
              guard: this.isNotMigratedGuard(),
            },
            {
              id: 'compare-invoices',
              type: LeftNavigationItemType.ExternalLink,
              permission: Permission.InvoiceComparisonTool,
              externalLink: of(environment.invoiceComparisonToolUrl),
            },
            {
              id: 'subscriptions',
              type: LeftNavigationItemType.Link,
              permission: Permission.BillingAndSubscriptions,
              guard: this.isNotMigratedGuard(),
            },
            {
              id: 'payment-management',
              type: LeftNavigationItemType.Link,
              permission: Permission.PaymentManagementView,
              guard: this.isNotMigratedGuard(
                toSignal(this.paymentManagementActiveFacade.isPaymentManagementActive$.pipe(filterIsNotUndefined(), first()), {
                  initialValue: false,
                })
              ),
            },
            {
              id: 'certify-tax',
              type: LeftNavigationItemType.ExternalLink,
              permission: Permission.CertifyTaxModify,
              ssoCertifyTaxLink: true,
              tagTextTranslationKey: 'navigation.tags.new',
              guard: this.isNotMigratedGuard(),
            },
          ],
        },
      ];
    }

    return [
      {
        id: 'subscriptions',
        type: LeftNavigationItemType.Link,
        iconClass: LeftNavigationIconClass.Subscriptions,
        permission: Permission.BillingAndSubscriptions,
      },
      {
        id: 'certify-tax',
        type: LeftNavigationItemType.ExternalLink,
        permission: Permission.CertifyTaxModify,
        ssoCertifyTaxLink: true,
        tagTextTranslationKey: 'navigation.tags.new',
      },
    ];
  }
}
